// RxJS 5.5, from the one rxjs copy thruway.js uses too, so these patch the Observable that
// $wamp.thruway.call returns. The subpaths carry .js for Node's ESM loader, which runs the
// tests; webpack takes either. (In the browser, services/thruway.js has already patched on
// every operator.)
import {Observable} from 'rxjs/Observable.js';
import 'rxjs/add/observable/of.js';
import 'rxjs/add/operator/catch.js';
import 'rxjs/add/operator/delay.js';
import 'rxjs/add/operator/map.js';
import 'rxjs/add/operator/retryWhen.js';
import 'rxjs/add/operator/scan.js';
import 'rxjs/add/operator/shareReplay.js';
import 'rxjs/add/operator/timeout.js';
import {isEarlierReport} from '../charts/marketToday.js';
import {notAuthorizedMessage} from './not-authorized.js';

// Ported from the mobile app's src/services/previousReport.ts. No Angular here, so Node can test
// it (charts/previousReport.test.mjs).

/**
 * @typedef {Object} PreviousReport
 * @property {import('../charts/marketToday.js').MarketTrade[]} trades
 * @property {string|null} reportDate As get_trades sends it: "m/d/y".
 */

const TIMEOUT = 25000;
const RETRIES = 2;
const RETRY_DELAY = 1000;

/** get_trades' answer, read defensively: [trades, "m/d/y"]. */
function parse(args) {
  const [trades, reportDate] = args || [];
  return {
    trades: Array.isArray(trades) ? trades : [],
    reportDate: typeof reportDate === 'string' ? reportDate : null,
  };
}

// The previous weekday's board (org.eggs.get_trades 'report11'), for Overview's market card to
// compare today's medians with. Its own call, not TradesService.load('report11'): that service
// pushes every report it loads into the Trades board's shared ReplaySubject and meta, so asking it
// for report11 would put the previous day's rows and date on the Trades board.
export default function PreviousReportService($wamp) {
  // One entry, for one board day. Angular makes a factory once per injector, so the app shares it.
  let cached = null;

  /**
   * The previous report for the board day `day` (the 'current' report's date), fetched once per day
   * and shared. Null when it can't be had (the card then shows no change); a failure isn't kept, so
   * the next call (the next board refresh) asks again. Nor is a report that isn't of an earlier day
   * than `day`: report11 is worked out from the server's clock when the call lands.
   * @param {string} day
   * @returns {Observable<PreviousReport|null>}
   */
  function get(day) {
    if (cached && cached.day === day) {
      return cached.report;
    }
    const entry = {day, report: Observable.of(null)};
    // Only its own entry: a late failure mustn't drop a newer day's.
    const forget = () => {
      if (cached === entry) {
        cached = null;
      }
    };
    // $wamp.thruway.call, not $wamp.call (a Promise): the raw result (args) as a cold RxJS 5
    // Observable, so every retry sends the call again.
    entry.report = $wamp.thruway.call('org.eggs.get_trades', ['report11'])
      .timeout(TIMEOUT)
      // A second apart, as the app retries (and Overview's own board call); the last error goes on to the catch.
      .retryWhen(errors => errors
        .scan((tries, error) => {
          // A refusal (no trading access) isn't tried again: the same login gets the same answer.
          if (tries >= RETRIES || notAuthorizedMessage(error)) {
            throw error;
          }
          return tries + 1;
        }, 0)
        .delay(RETRY_DELAY))
      .map(res => {
        const report = parse(res.args);
        if (isEarlierReport(day, report.reportDate)) {
          return report;
        }
        forget();
        return null;
      })
      .catch(() => {
        forget();
        return Observable.of(null);
      })
      .shareReplay(1);
    cached = entry;
    return entry.report;
  }

  return {get};
}
